/* 
    The purpose of this code is to marry the sprite and user information together
    to create a sprite for avatars in a web application. 
*/

import type { Point } from "./Types";
import type { FontStyle, UserData, BannerData } from "./Customization";

import { Sprite } from "./Sprite";
import { Animator } from "./Animator";
import { AvatarRenderer } from "./AvatarRenderer";
import { AvatarCanvas } from "./AvatarCanvas";
import { colorize } from "./ColorUtils";

export type AvatarState = "idle" | "walk" | "run" | "jump" | "die";

export class Avatar {
    public username: string = "";

    public sprite: Sprite;
    private _animator: Animator;

    public nameOffset = 32;

    public font: FontStyle = {
        name: "Arial",
        size: 20,
        color: "#FFFFFF",
    };

    private _banner: BannerData = {
        id: "",
        name: "",
    };
    get banner(): BannerData {
        return this._banner;
    }
    set banner(data: BannerData) {
        this._banner = data;
        if ((data.file?.length ?? 0) > 0) {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = data.file ? `${this._fileRoot}${data.file}` : "";
            img.onload = async () => {
                this.bannerImg = await colorize(img, data);
            };
        } else {
            this.bannerImg = null;
        }
    }

    private _state: AvatarState = "idle";

    private _location: Point = { x: 0, y: 0 };

    public disposed: boolean = false;

    public zIndex: number = 0;

    public bannerImg: HTMLImageElement | null = null;

    private _fileRoot: string = "";

    constructor(sprite: Sprite, animator: Animator) {
        this.sprite = sprite;
        this._animator = animator;
    }

    static fromData(data: UserData) {
        const sprite = new Sprite(data.avatar);
        const animator = new Animator();

        const av = new Avatar(sprite, animator);

        av._fileRoot = data.avatar.fileRoot ?? "";

        // Transfer values from data to AV
        if (data.username) av.username = data.username;
        if (data.avatar.font) av.font = data.avatar.font;
        if (data.avatar.banner) av.banner = data.avatar.banner;
        return av;
    }

    get animator(): Animator {
        return this._animator;
    }

    get state(): AvatarState {
        return this._state;
    }

    dispose() {
        this.disposed = true;
    }

    set state(state: AvatarState) {
        if (this._state === state) return;

        this._state = state;

        this._animator.play(`${this._state}`);
    }

    set location(loc: Point) {
        this._location = { ...loc };
    }

    get location(): Point {
        return this._location;
    }

    setLocation(location: Point) {
        this._location.x = location.x;
        this._location.y = location.y;
    }

    render(avCanvas: AvatarCanvas, delta: number) {
        this._animator.update(delta);
        const frame = this._animator.getCurrentFrame(); // frame from sprite animations

        AvatarRenderer.drawAvatar(avCanvas, this, frame, this.location, 2);
    }
}
